import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormHelperText, InputLabel, ListSubheader, MenuItem, Select,
  TextField, Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useDataProvider, useNotify } from 'react-admin';
import { DEFAULT_API_ADMIN } from '../../../constants';
import { getChainId } from '../../../lib';
import { request } from '../../../lib/request';
import { TagType } from '../../../models';
import { __t } from '../../../i18n/t';
import {
  ChainModules, HomeBlock, HomeNavigation, NAVIGATION_NONE_LABEL, ROW_ART, RowLayout,
  applyNavigationTarget, buildNavigationGroups, dateInputToIso, isoToDateInput,
  navigationChoiceKey, navigationKeyOf, navigationTargetIdOf, navigationTargetOf,
} from './homeItems';

interface BannerFormDialogProps {
  block: HomeBlock | null;
  layout: RowLayout;
  chain?: ChainModules | null;
  /**
   * `carousel`: card do carrossel. Mesmo contrato do bloco, mas aparece também no app
   * desatualizado, que lê o Remote Config — e ainda segmenta por grupo.
   */
  kind?: 'block' | 'carousel';
  onClose(): void;
  onSave(block: HomeBlock): void;
}

const METHODS = [
  { id: 'screen', name: 'Tela do app' },
  { id: 'webview', name: 'Site externo' },
  { id: 'whatsapp', name: 'WhatsApp' },
];

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

// Fora do JSX de propósito: o plugin de i18n embrulha literal em posição de prop, e
// traduzir um token de cor quebraria o overlay em espanhol.
const UPLOADING_OVERLAY_SX = {
  position: 'absolute' as const,
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'rgba(0,0,0,0.35)',
};

const SPINNER_SX = { color: 'common.white' };

const WINDOW_ROW_SX = { display: 'flex', gap: 2, mt: 2 };
const CHIPS_SX = { display: 'flex', flexWrap: 'wrap', gap: 0.5 };
// Discreto: é um lembrete ao lado do campo, não um bloqueio.
const TAG_HINT_SX = {
  mt: 1, py: 0.25, px: 1.25, fontSize: 12, alignItems: 'center',
  '& .MuiAlert-icon': { py: 0.5, mr: 1 },
  '& .MuiAlert-message': { py: 0.5 },
};
const SHRINK_LABEL = { shrink: true };

const BannerFormDialog: React.FC<BannerFormDialogProps> = ({
  block, layout, chain, kind = 'block', onClose, onSave,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const isCarousel = kind === 'carousel';

  const [imagePath, setImagePath] = useState<string | null>(null);
  const [navigation, setNavigation] = useState<HomeNavigation>({ method: 'screen', destination: '' });
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [tags, setTags] = useState<{ id: string; name: string }[]>([]);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [targetOptions, setTargetOptions] = useState<{ id: string; name: string }[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const art = ROW_ART[layout];
  const navigationKey = navigationKeyOf(navigation);
  // `navigationKey` entra como `keepKey`: destino já salvo de um módulo que a rede
  // desligou continua à vista, em vez de o seletor abrir em branco.
  const navigationGroups = useMemo(
    () => buildNavigationGroups(chain, navigationKey),
    [chain, navigationKey],
  );
  const navigationTarget = navigationTargetOf(navigationKey);
  const targetId = navigationTargetIdOf(navigation);

  useEffect(() => {
    if (!block) { return; }
    setImagePath(block.imagePath || null);
    setNavigation(block.navigation || { method: 'screen', destination: '' });
    setTagIds(block.tagIds || []);
    setGroupIds(block.groupIds || []);
    setStartAt(isoToDateInput(block.startAt));
    setEndAt(isoToDateInput(block.endAt));
    setUploadError(null);
  }, [block]);

  useEffect(() => {
    // Só etiqueta de cliente: a mesma tabela guarda etiqueta de posto e de produto.
    dataProvider.getList('chains/chainId/tag', {
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'name', order: 'ASC' },
      filter: { type: TagType.client },
    })
      .then(({ data }) => setTags((data || []) as { id: string; name: string }[]))
      .catch(() => setTags([]));
  }, [dataProvider]);

  useEffect(() => {
    if (!isCarousel) { return; }
    dataProvider.getList('chains/chainId/customer-groups', {
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'name', order: 'ASC' },
      filter: {},
    })
      .then(({ data }) => setGroups((data || []) as { id: string; name: string }[]))
      .catch(() => setGroups([]));
  }, [dataProvider, isCarousel]);

  const targetResource = navigationTarget?.resource;
  const targetSort = navigationTarget?.sort;

  useEffect(() => {
    if (!targetResource || !targetSort) { setTargetOptions([]); return; }
    setLoadingTargets(true);
    dataProvider.getList(targetResource, {
      // 200 é o TETO do backend, não um número escolhido: roletas e bolões fazem
      // `Math.min(take, 200)` e pedir mais é clampado em silêncio. Sem `pagination`
      // explícita o react-admin mandaria 25 — e o controller da roleta tem esse mesmo
      // 25 como default, então o seletor abriria cortado.
      pagination: { page: 1, perPage: 200 },
      sort: targetSort,
      filter: {},
    })
      // Registro sem `name` vira uma linha em branco no Select — cai no id, que ao
      // menos dá para conferir contra a lista do recurso.
      .then(({ data }) => setTargetOptions(
        (data || []).map((item: any) => ({ id: String(item.id), name: item.name || String(item.id) })),
      ))
      .catch(() => setTargetOptions([]))
      .finally(() => setLoadingTargets(false));
  }, [dataProvider, targetResource, targetSort]);

  // Item salvo que não voltou na lista (apagado, ou fora das 200 mais recentes) ainda
  // aparece, como o `keepKey` do destino faz: some do seletor seria some do bloco.
  const targetChoices = useMemo(() => {
    if (!targetId || targetOptions.some(option => option.id === targetId)) { return targetOptions; }
    return [...targetOptions, { id: targetId, name: `${targetId} — ${__t('item não encontrado')}` }];
  }, [targetOptions, targetId]);

  const tagNameById = useMemo(
    () => new Map(tags.map(tag => [tag.id, tag.name])),
    [tags],
  );
  const groupNameById = useMemo(
    () => new Map(groups.map(group => [group.id, group.name])),
    [groups],
  );

  const handlePickFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) { return; }

    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError('A imagem passa de 10MB. Comprima antes de subir.');
      return;
    }

    // A imagem sobe agora, sozinha, e o formulário guarda só a URL. É o que permite
    // vários blocos sem estourar o limite de payload do PATCH de configurações.
    setUploading(true);
    setUploadError(null);
    try {
      const body = new FormData();
      body.append('image', file);
      const response = await request<{ data?: { imagePath?: string }, imagePath?: string }>(
        `${DEFAULT_API_ADMIN}/chains/${getChainId()}/home-banner-image`,
        'POST',
        body,
      );
      const uploaded = response?.data?.imagePath || response?.imagePath;
      if (!uploaded) { throw new Error('Resposta sem o caminho da imagem.'); }
      setImagePath(uploaded);
    } catch (error) {
      const status = (error as { status?: number })?.status;
      setUploadError(status === 404
        ? 'A rota de upload ainda não existe nesta API. Depende da parte do backend.'
        : ((error as { message?: string })?.message || 'Não foi possível subir a imagem.'));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    if (!block) { return; }
    if (!imagePath) {
      notify(isCarousel ? __t('Escolha uma imagem para o card.') : __t('Escolha uma imagem para o bloco.'), { type: 'warning' });
      return;
    }
    // Site externo e WhatsApp sem destino viram toque morto: o app sai do
    // `openCardDestination` antes de abrir nada, e o bloco fica publicado sem ninguém
    // perceber. Tela do app tem a opção explícita de não levar a lugar nenhum.
    if (navigation.method !== 'screen' && !navigation.destination) {
      notify(isCarousel
        ? __t('Preencha o destino do toque ou deixe o card sem ação.')
        : __t('Preencha o destino do toque ou deixe o bloco sem ação.'), { type: 'warning' });
      return;
    }
    if (startAt && endAt && new Date(endAt) < new Date(startAt)) {
      notify('A data de fim da exibição é anterior à de início.', { type: 'warning' });
      return;
    }
    // `groupIds` sai do bloco ao salvar: é resto da segmentação por grupo, o diálogo não
    // o mostra mais, e deixá-lo gravado seria um filtro invisível por cima da etiqueta.
    // No carrossel o grupo continua à vista e editável, então fica.
    onSave({
      ...block,
      imagePath,
      navigation,
      tagIds,
      groupIds: isCarousel ? groupIds : undefined,
      startAt: dateInputToIso(startAt),
      endAt: dateInputToIso(endAt),
    });
  };

  const destinationField = () => {
    if (navigation.method === 'screen') {
      return (
        <>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Destino do toque</InputLabel>
            <Select
              value={navigationKey}
              label="Destino do toque"
              onChange={event => setNavigation(applyNavigationTarget(navigation, event.target.value, ''))}
            >
              {/*
                Lista achatada de propósito: o Select do MUI não aceita Fragment como
                filho (o Menu precisa enxergar cada MenuItem para medir e navegar pelo
                teclado), então o subcabeçalho e os itens entram no mesmo array.
              */}
              {[
                <MenuItem key="none" value="">{__t(NAVIGATION_NONE_LABEL)}</MenuItem>,
                ...navigationGroups.flatMap(group => [
                  <ListSubheader key={`group:${group.name}`}>{__t(group.name)}</ListSubheader>,
                  ...group.choices.map(choice => {
                    const key = navigationChoiceKey(choice);
                    return <MenuItem key={key} value={key}>{__t(choice.name)}</MenuItem>;
                  }),
                ]),
              ]}
            </Select>
          </FormControl>

          {navigationTarget && (
            <FormControl fullWidth size="small" sx={{ mt: 2 }} disabled={loadingTargets}>
              <InputLabel>{__t(navigationTarget.fieldName)}</InputLabel>
              <Select
                value={targetId}
                label={__t(navigationTarget.fieldName)}
                onChange={event => setNavigation(
                  applyNavigationTarget(navigation, navigationKey, event.target.value),
                )}
              >
                <MenuItem value="">{__t(navigationTarget.emptyName)}</MenuItem>
                {targetChoices.map(option => (
                  <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Escolhido um item, o toque abre direto nele em vez da lista.
              </FormHelperText>
            </FormControl>
          )}
        </>
      );
    }

    if (navigation.method === 'whatsapp') {
      return (
        <>
          <TextField
            fullWidth size="small" sx={{ mt: 2 }}
            label="Número de destino"
            value={navigation.destination || ''}
            onChange={event => setNavigation({ ...navigation, destination: event.target.value })}
          />
          <TextField
            fullWidth size="small" sx={{ mt: 2 }}
            label="Mensagem inicial"
            value={navigation.whatsappMessage || ''}
            onChange={event => setNavigation({ ...navigation, whatsappMessage: event.target.value })}
          />
        </>
      );
    }

    return (
      <TextField
        fullWidth size="small" sx={{ mt: 2 }}
        label="URL de destino"
        value={navigation.destination || ''}
        onChange={event => setNavigation({ ...navigation, destination: event.target.value })}
      />
    );
  };

  return (
    <Dialog open={!!block} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isCarousel ? __t('Imagem do carrossel') : __t('Bloco de imagem')}</DialogTitle>
      <DialogContent>

        <Typography variant="caption" color="text.secondary">
          {`Arte recomendada: ${art.width} × ${art.height} px. O texto faz parte da imagem, então use a medida deste tipo — arte de outro formato fica distorcida ou ilegível.`}
        </Typography>

        <Box
          sx={{
            mt: 1.5, width: '100%', aspectRatio: String(art.ratio),
            borderRadius: 1, border: '1px dashed', borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', bgcolor: 'action.hover', position: 'relative',
          }}
        >
          {imagePath
            ? <Box component="img" src={imagePath} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <Typography variant="body2" color="text.secondary">Nenhuma imagem escolhida</Typography>}
          {uploading && (
            <Box sx={UPLOADING_OVERLAY_SX}>
              <CircularProgress size={28} sx={SPINNER_SX} />
            </Box>
          )}
        </Box>

        <Button
          startIcon={<UploadFileIcon />}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          sx={{ mt: 1 }}
        >
          {imagePath ? 'Trocar imagem' : 'Escolher imagem'}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={handlePickFile}
        />

        {uploadError && <Alert severity="error" sx={{ mt: 1 }}>{uploadError}</Alert>}

        <FormControl fullWidth size="small" sx={{ mt: 3 }}>
          <InputLabel>Ação do toque</InputLabel>
          <Select
            value={navigation.method}
            label="Ação do toque"
            onChange={event => setNavigation({
              method: event.target.value as HomeNavigation['method'],
              destination: '',
            })}
          >
            {METHODS.map(method => (
              <MenuItem key={method.id} value={method.id}>{__t(method.name)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {destinationField()}

        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel>Exibir apenas para as etiquetas</InputLabel>
          <Select
            multiple
            value={tagIds}
            label="Exibir apenas para as etiquetas"
            onChange={event => setTagIds(
              typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value,
            )}
            renderValue={selected => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map(id => (
                  <Chip key={id} size="small" label={tagNameById.get(id) || id} />
                ))}
              </Box>
            )}
          >
            {tags.map(tag => (
              <MenuItem key={tag.id} value={tag.id}>{tag.name}</MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {isCarousel
              ? __t('Sem etiqueta selecionada, o card aparece para todos os clientes. Com mais de uma, basta o cliente ter qualquer uma delas.')
              : __t('Sem etiqueta selecionada, o bloco aparece para todos os clientes. Com mais de uma, basta o cliente ter qualquer uma delas.')}
          </FormHelperText>
        </FormControl>

        {/* O backend publica o card de etiqueta no Remote Config com um grupo que ninguém
            tem: o app antigo, que só entende grupo, esconde o card de todo mundo. */}
        {isCarousel && tagIds.length > 0 && (
          <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />} sx={TAG_HINT_SX}>
            Quem está em versão antiga do app não vê este card: a segmentação por etiqueta depende de o cliente atualizar o aplicativo.
          </Alert>
        )}

        {isCarousel && (
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Exibir apenas para os grupos</InputLabel>
            <Select
              multiple
              value={groupIds}
              label="Exibir apenas para os grupos"
              onChange={event => setGroupIds(
                typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value,
              )}
              renderValue={selected => (
                <Box sx={CHIPS_SX}>
                  {(selected as string[]).map(id => (
                    <Chip key={id} size="small" label={groupNameById.get(id) || id} />
                  ))}
                </Box>
              )}
            >
              {groups.map(group => (
                <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Segmentação antiga, que vale para todas as versões do app. Com etiqueta escolhida, o grupo deixa de valer.
            </FormHelperText>
          </FormControl>
        )}

        <Box sx={WINDOW_ROW_SX}>
          <TextField
            fullWidth
            size="small"
            type="datetime-local"
            label="Exibir a partir de"
            value={startAt}
            onChange={event => setStartAt(event.target.value)}
            InputLabelProps={SHRINK_LABEL}
          />
          <TextField
            fullWidth
            size="small"
            type="datetime-local"
            label="Exibir até"
            value={endAt}
            onChange={event => setEndAt(event.target.value)}
            InputLabelProps={SHRINK_LABEL}
          />
        </Box>
        <FormHelperText>
          {isCarousel
            ? __t('Em branco, o card não tem prazo. No app atualizado, ele entra e sai do ar na data. Em versão antiga, a troca só acontece quando alguém salvar esta tela depois da data.')
            : __t('Em branco, o bloco não tem prazo. Passada a data de fim, ele sai da Home sozinho — em aparelhos com o app desatualizado pode continuar aparecendo até a atualização.')}
        </FormHelperText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={uploading}>
          {isCarousel ? __t('Salvar imagem') : __t('Salvar bloco')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BannerFormDialog;
